import { DOCUMENT } from '@angular/common';
import { NgZone, Renderer2, RendererFactory2, inject } from '@angular/core';
import { Router, ViewTransitionInfo } from '@angular/router';

import { ContentReadyService, DeviceService, HtmlNode, Logger, WINDOW } from '@frontend/vanilla/core';
import { ViewTransitionStateService } from '@frontend/vanilla/core';
import { catchError, delay, merge, of, raceWith, switchMap, take, timer } from 'rxjs';

import { BrowserBackDetectionService } from './browser-back-detection/browser-back-detection.service';
import { NativeTransitionDetectionService } from './native-transition-detection/native-transition-detection.service';
import { isNavigationApplicationBack, isNavigationFlaggedSkipTransition, isNavigationToCurrentPage } from './view-transition-navigation.helper';
import {
    getHorizontalSlideModifiers,
    isAccountMenuSubNavigation,
    isExcludedRoute,
    isHorizontalSlideNavigation,
    isOverlayClose,
    isProductSwitchTransition,
} from './view-transition-route-modifiers';
import {
    DEFAULT_TRANSITION_CONFIG,
    VIEW_TRANSITION_LOG_TAG as LOG_TAG,
    PRODUCT_TRANSITION_CONFIG,
    TRANSITION_MODIFIERS,
    ZERO_DELAY_TRANSITION_CONFIG,
} from './view-transition.constants';

type Transition = ViewTransitionInfo['transition'];

let doc: Document;
let ngZone: NgZone;
let router: Router;
let logger: Logger;
let htmlNode: HtmlNode;
let windowRef: Window;
let contentReadyService: ContentReadyService;
let browserBackDetectionService: BrowserBackDetectionService;
let viewTransitionStateService: ViewTransitionStateService;
let nativeTransitionDetectionService: NativeTransitionDetectionService;
let renderer: Renderer2;
let deviceService: DeviceService;

let lastBackNavigation = 0;

export const onViewTransitionCreated = ({ transition }: ViewTransitionInfo) => {
    doc ??= inject(DOCUMENT);
    ngZone ??= inject(NgZone);
    router ??= inject(Router);
    logger ??= inject(Logger);
    htmlNode ??= inject(HtmlNode);
    windowRef ??= inject(WINDOW);
    contentReadyService ??= inject(ContentReadyService);
    browserBackDetectionService ??= inject(BrowserBackDetectionService);
    viewTransitionStateService ??= inject(ViewTransitionStateService);
    nativeTransitionDetectionService ??= inject(NativeTransitionDetectionService);
    renderer ??= inject(RendererFactory2).createRenderer(null, null);
    deviceService ??= inject(DeviceService);

    disableScrollRestoration();

    applyViewTransitionCss();
    handleViewTransitionState(transition);
    addDynamicViewTransitionBehaviour(transition);
};

/**
 * History API auto-scroll restoration is disabled with view transitions because it causes scrolling before the transition snapshot is ready, causing visual issues.
 */
const disableScrollRestoration = (): void => {
    if ('scrollRestoration' in windowRef.history) {
        windowRef.history.scrollRestoration = 'manual';
    }
};

const applyViewTransitionCss = (): void => {
    htmlNode.setCssClass('view-transitions', true);
};

const handleViewTransitionState = (transition: Transition): void => {
    viewTransitionStateService.setScheduled(true);

    transition.ready
        .then(() => {
            viewTransitionStateService.setReady();
        })
        .catch((error) => {
            viewTransitionStateService.setFinished();

            logger.error(`${LOG_TAG} Transition threw before ready: ${error}`);
        })
        .finally(() => {
            viewTransitionStateService.setScheduled(false);

            /** Ensure product switches scroll to top correctly, but only after the snapshots are in place. */
            if (viewTransitionStateService.isProductSwitch) {
                windowRef.scrollTo({ top: 0 });
            }
        });

    transition.finished
        .catch((error) => {
            logger.error(`${LOG_TAG} Transition threw before finishing: ${error}`);
        })
        .finally(() => {
            viewTransitionStateService.setFinished();

            resetTransitionModifiers();
        });
};

const handleViewTransitionDelay = (): void => {
    pauseViewTransition();

    ngZone.runOutsideAngular(() => {
        const { transitionDelay, contentStartTimeout } = getTransitionTimeouts();

        const contentStart$ = contentReadyService.contentStart.pipe(raceWith(timer(contentStartTimeout)), take(1));

        merge(viewTransitionStateService.whenReady, viewTransitionStateService.whenFinished)
            .pipe(
                take(1),
                switchMap(() => contentStart$),
                delay(transitionDelay),
                catchError((error: unknown) => {
                    logger.error(`${LOG_TAG} Error handling view transition delay: ${error}`);

                    return of(null);
                }),
            )
            .subscribe(() => playViewTransition());
    });
};

const getTransitionTimeouts = (): { transitionDelay: number; contentStartTimeout: number } => {
    if (isProductSwitchTransition()) {
        return PRODUCT_TRANSITION_CONFIG;
    }

    if (isOverlayClose() || isAccountMenuSubNavigation()) {
        return ZERO_DELAY_TRANSITION_CONFIG;
    }

    return DEFAULT_TRANSITION_CONFIG;
};

const pauseViewTransition = (): void => {
    htmlNode.setCssClass('view-transition-paused', true);
};

const playViewTransition = (): void => {
    htmlNode.setCssClass('view-transition-paused', false);
};

const addDynamicViewTransitionBehaviour = (transition: Transition): void => {
    if (shouldSkipTransition()) {
        transition.skipTransition();

        return;
    }

    handleViewTransitionDelay();
    useDocumentHeightPreservation(transition);
    useRouteSpecificTransitions();
    useDirectionalTransitions();
};

const shouldSkipTransition = (): boolean => {
    if (isNavigationFlaggedSkipTransition()) {
        logger.info(`${LOG_TAG} Skipping as skipTransition flag is set`);

        return true;
    }

    if (isNavigationToCurrentPage()) {
        logger.info(`${LOG_TAG} Skipping as navigation is to current page`);

        return true;
    }

    if (isExcludedRoute()) {
        logger.info(`${LOG_TAG} Skipping as route is explicitly excluded from view transitions`);

        return true;
    }

    if (nativeTransitionDetectionService.strategy?.navigationHasNativeTransition) {
        logger.info(`${LOG_TAG} Skipping due to native transition`);

        return true;
    }

    return false;
};

/**
 * iOS only.
 *
 * When navigating from a long page (e.g. 10,000px) to a short one (e.g. 1000px), viewport scrolling implicitly occurs
 * due to the document changing length - the viewport moves upwards, or is 'scrolled up'. This is not handled well by Safari
 * during view transitions. Safari applies the view transition snapshots on top of the viewport, and it seems that, if the
 * viewport moves very quickly, there is a slight delay in the snapshot being moved to the new viewport position.
 *
 * This results in a brief moment where you see a flash of some other content. In my experimentation, you may see some content
 * that was 'above' the current position, but if the movement is large you will just see empty space. Likely because either Safari
 * has unrendered content significantly outside the viewport during the transition or because the components have been destroyed as part
 * of the navigation already.
 *
 * The solution is to prevent the document length shortening during the view transition. Since we scroll to the top of the page during
 * navigations, brief excess length below the footer shouldn't be noticeable. Navigating from short pages to longer ones is not an issue.
 */
const useDocumentHeightPreservation = (transition: Transition): void => {
    if (!deviceService.isiOS) {
        return;
    }

    const documentHeight = doc.body.scrollHeight;

    renderer.setStyle(doc.body, 'min-height', `${documentHeight}px`);

    transition.finished
        .catch((error) => {
            logger.error(`${LOG_TAG} Transition threw before finishing: ${error}`);
        })
        .finally(() => {
            renderer.removeStyle(doc.body, 'min-height');
        });
};

const useRouteSpecificTransitions = (): void => {
    if (isHorizontalSlideNavigation()) {
        logger.info(`${LOG_TAG} Using horizontal slide transition animation`);

        htmlNode.setCssClass(TRANSITION_MODIFIERS.HORIZONTAL_SLIDE, true);

        getHorizontalSlideModifiers().forEach((modifier) => htmlNode.setCssClass(modifier, true));
    } else if (isProductSwitchTransition()) {
        logger.info(`${LOG_TAG} Using product switch transition animation`);

        htmlNode.setCssClass(TRANSITION_MODIFIERS.PRODUCT_SWITCH, true);
    } else {
        logger.info(`${LOG_TAG} Using default transition animation`);

        htmlNode.setCssClass(TRANSITION_MODIFIERS.DEFAULT, true);
    }
};

const useDirectionalTransitions = (debounce = DEFAULT_TRANSITION_CONFIG.transitionDelay): void => {
    if (isBackNavigation()) {
        logger.info(`${LOG_TAG} Using backwards transition animation`);

        htmlNode.setCssClass(TRANSITION_MODIFIERS.BACKWARDS, true);

        lastBackNavigation = performance.now();

        return;
    }

    /** Prevent back navigation state from being unset immediately if multiple calls are made in quick succession */
    if (performance.now() - lastBackNavigation > debounce) {
        htmlNode.setCssClass(TRANSITION_MODIFIERS.BACKWARDS, false);
    }
};

const isBackNavigation = (): boolean => {
    return isBrowserBackNavigation() || isApplicationBackNavigation();
};

const isBrowserBackNavigation = (): boolean => {
    return Boolean(browserBackDetectionService.strategy?.isBrowserBackNavigation());
};

const isApplicationBackNavigation = (): boolean => {
    return isNavigationApplicationBack();
};

const resetTransitionModifiers = (): void => {
    Object.values(TRANSITION_MODIFIERS).forEach((modifier) => htmlNode.setCssClass(modifier, false));
};
